/**
 * Listagem de Configurações de URA (Unidade de Resposta Audível)
 * VigosCloud - URA System
 * 
 * Lista completa com filtros e ações
 */

import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { 
  accessLocalStorage
} from '../../../services';
import * as URAApi from '../../../services/uraApi';

import Loading from '../../Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faEdit,
  faTrash,
  faPhoneVolume,
  faSearch,
  faSync,
  faCheckCircle,
  faTimesCircle,
  faSitemap,
  faListOl
} from '@fortawesome/free-solid-svg-icons';

import '../../../libs/bulma.min.css';
import './URA.css';

function URAList() {
  const history = useHistory();
  
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [userTenant, setUserTenant] = useState('');
  const [userRole, setUserRole] = useState('');
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    activeOnly: true
  });
  
  // Modal de confirmação de exclusão
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    configId: null,
    contextName: ''
  });
  
  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  // Recarregar quando filtros mudarem
  useEffect(() => {
    if (userTenant) {
      loadConfigs();
    }
  }, [userTenant, filters]);

  const loadInitialData = async () => {
    try {
      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const tenant = user.user.idtenant;
      const role = user.user.role;
      setUserTenant(tenant);
      setUserRole(role);

      console.log('🔍 [URA-LIST] Carregando dados para tenant:', tenant);
    } catch (error) {
      console.error('❌ [URA-LIST] Erro ao carregar dados iniciais:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    try {
      setLoading(true);

      const requestFilters = {
        tenant: userTenant,
        activeOnly: filters.activeOnly
      };

      console.log('🔍 [URA-LIST] Buscando URAs com filtros:', requestFilters);

      const response = await URAApi.getAllURAConfigs(requestFilters);

      if (response.success) {
        let filteredConfigs = response.data || [];

        // Aplicar filtro de busca local
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filteredConfigs = filteredConfigs.filter(config => 
            config.context_name?.toLowerCase().includes(searchLower) ||
            config.description?.toLowerCase().includes(searchLower) ||
            config.menu_extension?.toLowerCase().includes(searchLower)
          );
        }

        setConfigs(filteredConfigs);
        console.log(`✅ [URA-LIST] ${filteredConfigs.length} URAs carregadas`);
      } else {
        console.error('❌ [URA-LIST] Erro ao carregar URAs:', response.error);
        setConfigs([]);
      }

    } catch (error) {
      console.error('❌ [URA-LIST] Erro ao carregar URAs:', error);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefresh = () => {
    loadConfigs();
  };

  const handleDelete = async () => {
    if (!deleteModal.configId) return;

    try {
      setLoading(true);
      console.log(`🗑️ [URA-LIST] Deletando URA ID: ${deleteModal.configId}`);

      const response = await URAApi.deleteURAConfig(deleteModal.configId);

      if (response.success) {
        console.log('✅ [URA-LIST] URA deletada com sucesso');
        
        showAlert('success', 'URA deletada com sucesso!');
        
        // Recarregar lista
        await loadConfigs();
      } else {
        console.error('❌ [URA-LIST] Erro ao deletar URA:', response.error);
        showAlert('danger', response.error || 'Erro ao deletar URA');
      }

      // Fechar modal
      setDeleteModal({ show: false, configId: null, contextName: '' });

    } catch (error) {
      console.error('❌ [URA-LIST] Erro ao deletar URA:', error);
      showAlert('danger', 'Erro ao deletar URA');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const confirmDelete = (config) => {
    setDeleteModal({
      show: true,
      configId: config.id,
      contextName: config.context_name
    });
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, configId: null, contextName: '' });
  };

  if (loading && configs.length === 0) {
    return <Loading />;
  }

  return (
    <div className="ura-container">
      <div className="ura-header">
        <div className="ura-title-wrapper">
          <svg className="ura-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
          <div>
            <h1 className="ura-title">URAs (Unidade de Resposta Audível)</h1>
            <p className="ura-subtitle">Gerencie menus de atendimento interativo</p>
          </div>
        </div>
        <div className="ura-actions">
          <Link to="/config/ura/create" className="btn-new-ura">
            <FontAwesomeIcon icon={faPlus} />
            Nova URA
          </Link>
        </div>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : alert.type === 'danger' ? 'Erro!' : 'Atenção!'}</span>
              <button className="alert-close" onClick={() => setAlert({ ...alert, show: false })}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        </div>
      )}

      <div className="filter-card">
        <div className="filter-row">
          <div className="filter-field filter-field-large">
            <label className="filter-label">Buscar</label>
            <div className="filter-input-icon">
              <FontAwesomeIcon icon={faSearch} className="filter-icon" />
              <input
                className="filter-input"
                type="text"
                placeholder="Buscar por nome do contexto, descrição ou extensão do menu"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
          
          <div className="filter-field">
            <label className="filter-label">Status</label>
            <select
              className="filter-select"
              value={filters.activeOnly ? 'active' : 'all'}
              onChange={(e) => handleFilterChange('activeOnly', e.target.value === 'active')}
            >
              <option value="all">Todos</option>
              <option value="active">Apenas Ativos</option>
            </select>
          </div>
          
          <div className="filter-field">
            <label className="filter-label">&nbsp;</label>
            <button className="btn-refresh" onClick={handleRefresh}>
              <FontAwesomeIcon icon={faSync} />
              Atualizar
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : configs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faPhoneVolume} />
          </div>
          <p className="empty-state-text">Nenhuma URA encontrada. Clique em "Nova URA" para criar a primeira configuração.</p>
        </div>
      ) : (
        <div className="ura-table-wrapper">
          <table className="ura-table">
            <thead className="ura-table-header">
              <tr>
                <th>Nome do Contexto</th>
                <th>Descrição</th>
                <th>Extensão Menu</th>
                <th>Extensões</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => (
                <tr key={config.id}>
                  <td>
                    <div className="ura-context-name">{config.context_name}</div>
                    <div className="ura-context-id">ID: {config.id}</div>
                  </td>
                  <td>
                    {config.description || <span style={{color: '#bdc3c7'}}>Sem descrição</span>}
                  </td>
                  <td>
                    <span className="ura-badge badge-extension">{config.menu_extension || 'menu'}</span>
                  </td>
                  <td>
                    <span className="ura-badge badge-count">
                      <FontAwesomeIcon icon={faListOl} style={{ marginRight: '5px' }} />
                      {config.extension_count || 0}
                    </span>
                  </td>
                  <td>
                    <span className={`ura-badge ${config.active ? 'badge-active' : 'badge-inactive'}`}>
                      <FontAwesomeIcon icon={config.active ? faCheckCircle : faTimesCircle} style={{ marginRight: '5px' }} />
                      {config.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <div className="ura-actions-cell">
                      <Link
                        to={`/config/ura/edit/${config.id}`}
                        className="btn-ura-action btn-edit-ura"
                        title="Editar"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      <button
                        className="btn-ura-action btn-delete-ura"
                        onClick={() => confirmDelete(config)}
                        title="Deletar"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteModal.show && (
        <div className="modal is-active">
          <div className="modal-background" onClick={cancelDelete}></div>
          <div className="modal-card">
            <header className="modal-card-head has-background-danger">
              <p className="modal-card-title has-text-white">
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '10px' }} />
                Confirmar Exclusão
              </p>
              <button className="delete" aria-label="close" onClick={cancelDelete}></button>
            </header>
            <section className="modal-card-body">
              <div className="content">
                <p>Você está prestes a deletar a URA:</p>
                <div className="box has-background-light">
                  <p className="is-size-5 has-text-weight-bold">{deleteModal.contextName}</p>
                </div>
                <p className="has-text-danger has-text-weight-bold">
                  ⚠️ Esta ação não pode ser desfeita!
                </p>
                <p>
                  O arquivo de configuração será removido do Asterisk e o dialplan será recarregado.
                </p>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button className="button is-danger" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                Sim, Deletar URA
              </button>
              <button className="button" onClick={cancelDelete}>Cancelar</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default URAList;

